'use client';
/**
 * CHOISIR UN CONTACT DE L'ANNUAIRE — le tiers d'une non-conformité, d'une réclamation, d'un produit, d'un lot,
 * d'une revue de contrat, d'un compte partenaire, d'une convention de passage.
 *
 * Une LISTE, jamais un champ libre (RQ, 07/10/2026). Le relevé mensuel groupe les non-conformités par la RÉFÉRENCE
 * du correspondant : saisi à la main, le même centre de dialyse s'écrirait de quatre façons et le relevé compterait
 * faux — en silence.
 *
 * ⚠️ BASCULE DU 20/10/2026 : le carnet de contacts est SUPPRIMÉ. La valeur est la référence d'une fiche de
 * l'annuaire (`ORG-0151`, `PER-0284`). Près de 1 700 fiches : on CHERCHE (nom, téléphone, CNAM…) ; une famille
 * courte (fournisseurs, laboratoires) s'offre encore en liste. Les familles sont calculées par le serveur.
 */
import React from 'react';
import { inputStyle, Selecteur } from '@/components/ui';

export interface ContactBref { id: string; nom: string; actif: boolean; familles: string[]; detail?: string }
/** Ancien nom (le carnet), gardé pour les écrans qui typent une valeur choisie. */
export type CorrespondantBref = ContactBref;

const noms = new Map<string, ContactBref>();
async function lireJson<T>(chemin: string): Promise<T | null> {
  try { const r = await fetch(`/api/ged/${chemin}`, { credentials: 'include' }); return r.ok ? (await r.json()) as T : null; } catch { return null; }
}

/** Les fiches de ces références (nom, familles, actif), mises en cache pour la séance. */
export async function contactsParIds(ids: readonly string[]): Promise<Map<string, ContactBref>> {
  const manquants = [...new Set(ids.filter((x) => x && !noms.has(x)))];
  if (manquants.length) {
    const j = await lireJson<{ contacts: ContactBref[] }>(`ged/annuaire/contacts?ids=${encodeURIComponent(manquants.join(','))}`);
    for (const c of j?.contacts || []) noms.set(c.id, c);
  }
  return new Map(ids.filter((x) => noms.has(x)).map((x) => [x, noms.get(x)!]));
}

/** Le nom de chaque référence, pour un écran qui AFFICHE des contacts déjà choisis (tableaux, fiches). */
export function useNomsContacts(ids: readonly string[]): Map<string, ContactBref> {
  const cle = [...new Set(ids.filter(Boolean))].sort().join(',');
  const [carte, setCarte] = React.useState<Map<string, ContactBref>>(() => new Map());
  React.useEffect(() => {
    if (!cle) return;
    let vivant = true;
    void contactsParIds(cle.split(',')).then((m) => { if (vivant) setCarte(m); });
    return () => { vivant = false; };
  }, [cle]);
  return carte;
}

/** Une famille courte (≤ 400 fiches), en liste ; `null` tant qu'elle n'est pas lue, `[]` si elle est trop longue. */
function useFamilles(familles: readonly string[] | undefined, actif: boolean): { liste: ContactBref[] | null; trop: boolean } {
  const cle = (familles || []).join(',');
  const [etat, setEtat] = React.useState<{ cle: string; liste: ContactBref[] | null; trop: boolean }>({ cle: '', liste: null, trop: false });
  React.useEffect(() => {
    if (!actif || !cle) return;
    let vivant = true;
    void lireJson<{ contacts: ContactBref[]; trop?: boolean }>(`ged/annuaire/contacts?familles=${encodeURIComponent(cle)}`).then((j) => {
      if (!vivant) return;
      for (const c of j?.contacts || []) noms.set(c.id, c);
      setEtat({ cle, liste: j?.contacts || [], trop: !!j?.trop || !j });
    });
    return () => { vivant = false; };
  }, [cle, actif]);
  return etat.cle === cle ? etat : { liste: cle ? null : [], trop: false };
}

export function ChoixCorrespondant({ id, valeur, onChange, actif = true, aide, familles, exclure, inclure, vide }: {
  id: string;
  /** La référence de la fiche (`ORG-…`, `PER-…`), ou vide. */
  valeur: string;
  onChange: (reference: string) => void;
  /** Faux quand le champ n'est pas affiché : inutile d'appeler le serveur. */
  actif?: boolean;
  /** Phrase affichée sous le champ. Vide : la phrase par défaut. */
  aide?: React.ReactNode;
  /** Ne proposer que ces familles (laboratoires, cliniques, preleveurs, fournisseurs, assurances). */
  familles?: readonly string[];
  /** Références à ne pas proposer (déjà prises ailleurs). */
  exclure?: readonly string[];
  /** Ne proposer QUE ces références (16/10/2026 : les fournisseurs du produit choisi). Vide : pas de restriction. */
  inclure?: readonly string[];
  /** Le libellé du choix vide. */
  vide?: string;
}) {
  const choisi = useNomsContacts(valeur ? [valeur, ...(inclure || [])] : [...(inclure || [])]);
  const famille = useFamilles(inclure?.length ? undefined : familles, actif);
  const [q, setQ] = React.useState('');
  const [res, setRes] = React.useState<ContactBref[]>([]);
  const [ouvert, setOuvert] = React.useState(false);
  const minuterie = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const filtrer = (l: ContactBref[]) => l.filter((c) => (c.actif || c.id === valeur) && (!exclure || c.id === valeur || !exclure.includes(c.id)));
  const actuel = valeur ? choisi.get(valeur) : undefined;
  const sousAide = <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>{aide || 'Renseigné, il fait entrer cette non-conformité dans le relevé mensuel du correspondant.'}</div>;

  /* Une LISTE quand elle est courte : les fournisseurs du produit, ou une famille de quelques centaines de fiches. */
  const liste = inclure?.length ? inclure.map((x) => choisi.get(x)).filter((x): x is ContactBref => !!x) : !famille.trop ? famille.liste : null;
  if (liste) {
    const offertes = filtrer(liste);
    return (
      <>
        <Selecteur id={id} value={valeur} onChange={onChange} style={inputStyle}>
          <option value="">{vide || '— aucun (non-conformité interne)'}</option>
          {offertes.map((l) => <option key={l.id} value={l.id}>{l.nom}{l.actif ? '' : ' (désactivée)'}</option>)}
          {/* Une référence choisie que la liste ne propose plus : on l'AFFICHE au lieu de l'effacer. */}
          {valeur && !offertes.some((l) => l.id === valeur) && <option value={valeur}>{actuel ? actuel.nom : valeur}{actuel && !actuel.actif ? ' (désactivée)' : ''}</option>}
        </Selecteur>
        {sousAide}
      </>
    );
  }
  /* Sinon on CHERCHE dans l'annuaire. */
  const chercher = (texte: string) => {
    if (minuterie.current) clearTimeout(minuterie.current);
    if (texte.trim().length < 2) { setRes([]); return; }
    minuterie.current = setTimeout(() => {
      void lireJson<{ contacts: ContactBref[] }>(`ged/annuaire/contacts?q=${encodeURIComponent(texte)}${familles?.length ? `&familles=${encodeURIComponent(familles.join(','))}` : ''}`)
        .then((j) => { for (const c of j?.contacts || []) noms.set(c.id, c); setRes(filtrer(j?.contacts || [])); });
    }, 250);
  };
  return (
    <div style={{ position: 'relative' }}>
      <input id={id} role="combobox" aria-expanded={ouvert} aria-controls={`${id}-liste`} autoComplete="off"
        value={ouvert ? q : (actuel ? actuel.nom : (valeur || ''))}
        placeholder={vide || 'Chercher dans l’annuaire : nom, téléphone, CNAM…'}
        onFocus={() => { setOuvert(true); setQ(''); }} onBlur={() => setTimeout(() => setOuvert(false), 150)}
        onChange={(e) => { setQ(e.target.value); chercher(e.target.value); }} style={inputStyle} />
      {ouvert && (res.length > 0 || valeur) && (
        <ul id={`${id}-liste`} role="listbox" style={{ position: 'absolute', zIndex: 30, insetInlineStart: 0, insetInlineEnd: 0, margin: '4px 0 0', padding: 4, listStyle: 'none',
          background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', maxHeight: 280, overflowY: 'auto' }}>
          {valeur && (
            <li role="option" aria-selected={false}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange(''); setOuvert(false); }}
                style={{ width: '100%', textAlign: 'start', padding: '7px 10px', border: 0, background: 'transparent', cursor: 'pointer', font: 'inherit', color: 'var(--text-muted)' }}>{vide || '— aucun'}</button>
            </li>
          )}
          {res.map((c) => (
            <li key={c.id} role="option" aria-selected={c.id === valeur}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange(c.id); setOuvert(false); }}
                style={{ display: 'grid', width: '100%', textAlign: 'start', padding: '7px 10px', border: 0, background: 'transparent', borderRadius: 'var(--radius-sm)', cursor: 'pointer', font: 'inherit' }}>
                <span style={{ color: 'var(--text-strong)', fontWeight: 600 }}>{c.nom}</span>
                {c.detail && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.detail}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {sousAide}
    </div>
  );
}
